import React, { useRef, useState, useEffect } from 'react';
import { AiFillStar, AiFillHeart, AiOutlineHeart } from 'react-icons/ai';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { MdFlashOn, MdLocalOffer } from 'react-icons/md';
import { BsShieldFillCheck } from 'react-icons/bs';
import { TRANSPARENT_PLACEHOLDER } from '../../utils/constants';

// ─── Types ────────────────────────────────────────────────────────────────────
interface DealProduct {
  id: number;
  name: string;
  brand: string;
  image: string;
  price: number;
  originalPrice: number;
  rating: number;
  ratingCount: string;
  badge?: string;
  tag?: string;
  assured?: boolean;
  category: string;
}

interface DealCategory {
  id: number;
  label: string;
  icon: string;
}

// ─── Static Data ──────────────────────────────────────────────────────────────
const dealCategories: DealCategory[] = [
  { id: 0, label: 'All', icon: '🔥' },
  { id: 1, label: 'Fashion', icon: '👗' },
  { id: 2, label: 'Electronics', icon: '📱' },
  { id: 3, label: 'Home', icon: '🏠' },
  { id: 4, label: 'Beauty', icon: '💄' },
  { id: 5, label: 'Sports', icon: '🏋️' },
];

const dealProducts: DealProduct[] = [
  {
    id: 1,
    name: "Men's Slim Fit Casual Shirt – Premium Cotton",
    brand: 'PUMA',
    image: 'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=400&q=80',
    price: 599, originalPrice: 1999,
    rating: 4.3, ratingCount: '12.4K',
    badge: 'BESTSELLER', tag: 'FREE DELIVERY', assured: true,
    category: 'Fashion',
  },
  {
    id: 2,
    name: 'Wireless Bluetooth Earbuds – 40hr Playtime',
    brand: 'boAt',
    image: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=400&q=80',
    price: 999, originalPrice: 3499,
    rating: 4.5, ratingCount: '89.2K',
    badge: 'TRENDING', tag: 'EXTRA 10% OFF', assured: true,
    category: 'Electronics',
  },
  {
    id: 3,
    name: "Women's Embroidered Kurti Set – Festive",
    brand: 'Libas',
    image: 'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&q=80',
    price: 449, originalPrice: 1299,
    rating: 4.1, ratingCount: '6.7K',
    badge: 'NEW', tag: 'FREE DELIVERY', assured: false,
    category: 'Fashion',
  },
  {
    id: 4,
    name: 'Smart LED Desk Lamp with USB Charging Port',
    brand: 'Philips',
    image: 'https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=400&q=80',
    price: 799, originalPrice: 1999,
    rating: 4.4, ratingCount: '3.1K',
    badge: 'BESTSELLER', tag: 'EXTRA 5% OFF', assured: true,
    category: 'Home',
  },
  {
    id: 5,
    name: "Men's Running Shoes – Lightweight Comfort",
    brand: 'Nike',
    image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80',
    price: 1299, originalPrice: 3999,
    rating: 4.6, ratingCount: '45.8K',
    badge: 'TRENDING', tag: 'FREE DELIVERY', assured: true,
    category: 'Sports',
  },
  {
    id: 6,
    name: 'Vitamin C Face Serum 30ml – Anti-Aging Glow',
    brand: 'Minimalist',
    image: 'https://images.unsplash.com/photo-1570194065650-d99fb4bedf0a?w=400&q=80',
    price: 349, originalPrice: 899,
    rating: 4.2, ratingCount: '24.1K',
    badge: 'BESTSELLER', tag: 'BUY 2 GET 1', assured: false,
    category: 'Beauty',
  },
  {
    id: 7,
    name: 'Stainless Steel Insulated Water Bottle 1L',
    brand: 'Milton',
    image: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400&q=80',
    price: 299, originalPrice: 799,
    rating: 4.3, ratingCount: '18.6K',
    badge: 'HOT DEAL', tag: 'FREE DELIVERY', assured: true,
    category: 'Home',
  },
  {
    id: 8,
    name: "Women's Sports Leggings – 4-Way Stretch",
    brand: 'Jockey',
    image: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=400&q=80',
    price: 499, originalPrice: 1299,
    rating: 4.0, ratingCount: '7.9K',
    badge: 'NEW', tag: 'EXTRA 10% OFF', assured: false,
    category: 'Sports',
  },
  {
    id: 9,
    name: 'Smart Watch – Health Monitoring & GPS',
    brand: 'Fire-Boltt',
    image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&q=80',
    price: 1499, originalPrice: 4999,
    rating: 4.4, ratingCount: '32.5K',
    badge: 'TRENDING', tag: 'FREE DELIVERY', assured: true,
    category: 'Electronics',
  },
  {
    id: 10,
    name: 'Ayurvedic Rose Face Wash – Natural Glow',
    brand: 'Himalaya',
    image: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=400&q=80',
    price: 199, originalPrice: 549,
    rating: 4.5, ratingCount: '41.2K',
    badge: 'BESTSELLER', tag: 'BUY 3 GET 1', assured: false,
    category: 'Beauty',
  },
];

const badgeStyle: Record<string, { bg: string; color: string }> = {
  BESTSELLER: { bg: '#fff3e0', color: '#e65100' },
  TRENDING: { bg: '#fce4ec', color: '#c2185b' },
  NEW: { bg: '#e8f5e9', color: '#2e7d32' },
  'HOT DEAL': { bg: '#ffebee', color: '#b71c1c' },
};

// ─── Countdown Hook ───────────────────────────────────────────────────────────
function useCountdown(hours: number) {
  const end = useRef(Date.now() + hours * 3600_000);
  const [t, setT] = useState({ h: hours, m: 0, s: 0 });
  useEffect(() => {
    const id = setInterval(() => {
      const d = Math.max(0, end.current - Date.now());
      setT({ h: Math.floor(d / 3600000), m: Math.floor((d % 3600000) / 60000), s: Math.floor((d % 60000) / 1000) });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

// ─── Deal Card ────────────────────────────────────────────────────────────────
const DealCard: React.FC<{ product: DealProduct }> = ({ product }) => {
  const [wish, setWish] = useState(false);
  const [added, setAdded] = useState(false);
  const disc = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
  const bs = badgeStyle[product.badge || ''] ?? { bg: '#e3f2fd', color: '#0d47a1' };

  const handleCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="mds-card">
      {/* Discount ribbon */}
      <div className="mds-ribbon">{disc}%<br />OFF</div>

      {/* Wishlist */}
      <button className="mds-wish-btn" onClick={e => { e.stopPropagation(); setWish(w => !w); }} aria-label="Wishlist">
        {wish ? <AiFillHeart size={17} color="#f44336" /> : <AiOutlineHeart size={17} color="#999" />}
      </button>

      {/* Badge */}
      {product.badge && (
        <div className="mds-badge" style={{ background: bs.bg, color: bs.color }}>{product.badge}</div>
      )}

      {/* Image */}
      <div className="mds-img-wrap">
        <img
          src={product.image}
          alt={product.name}
          className="mds-img"
          onError={e => { (e.target as HTMLImageElement).src = TRANSPARENT_PLACEHOLDER; }}
        />
      </div>

      {/* Body */}
      <div className="mds-body">
        <div className="mds-brand">{product.brand}</div>
        <div className="mds-name" title={product.name}>{product.name}</div>

        {/* Rating */}
        <div className="mds-rating-row">
          <span className="mds-rating-pill">{product.rating} <AiFillStar size={10} /></span>
          <span className="mds-rating-count">({product.ratingCount})</span>
          {product.assured && (
            <span className="mds-assured"><BsShieldFillCheck size={11} /> Assured</span>
          )}
        </div>

        {/* Pricing */}
        <div className="mds-price-row">
          <span className="mds-price">₹{product.price.toLocaleString()}</span>
          <span className="mds-mrp">₹{product.originalPrice.toLocaleString()}</span>
          <span className="mds-off">{disc}% off</span>
        </div>

        {/* Tag */}
        {product.tag && (
          <div className="mds-tag-pill"><MdLocalOffer size={11} /> {product.tag}</div>
        )}

        {/* ── button-48 style – same as ProductCard ── */}
        <button
          className={`mds-btn48${added ? ' mds-btn48--added' : ''}`}
          onClick={handleCart}
          role="button"
        >
          <span className="mds-btn48__text">{added ? '✓ Added!' : 'Add to Cart'}</span>
        </button>
      </div>
    </div>
  );
};
// ─── Hover Promo Card ────────────────────────────────────────────────────────
const HoverCarouselCard = () => {
  const promoImages = [
    "https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&q=80",
    "https://images.unsplash.com/photo-1526406915894-7bcd65f60845?w=600&q=80",
    "https://images.unsplash.com/photo-1542393545-10f5cde2c810?w=600&q=80",
    "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&q=80",
    "https://images.unsplash.com/photo-1555664424-778a1e5e1b48?w=600&q=80"
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % promoImages.length);
    }, 2200); // Premium pacing of 2.2 seconds per slide
    return () => clearInterval(interval);
  }, [promoImages.length]);

  return (
    <div
      className="mds-promo-card"
      style={{ overflow: 'hidden', position: 'relative' }}
    >
      <div style={{
        display: 'flex', height: '100%', width: '100%',
        transform: `translateX(-${index * 100}%)`,
        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {promoImages.map((src, i) => (
          <img key={i} src={src} alt="Electronics" style={{ width: '100%', height: '100%', objectFit: 'cover', flexShrink: 0 }} />
        ))}
      </div>
      <div className="mds-promo-overlay" style={{ pointerEvents: 'none', background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.1) 100%)' }}>
        <div className="mds-promo-title" style={{ fontSize: '24px', marginBottom: '4px' }}>Electronics Fest</div>
        <div className="mds-promo-sub" style={{ fontSize: '16px', color: '#00b259', fontWeight: 'bold' }}>Up to 65% Off</div>
      </div>

      {/* Carousel Dots - Always visible for a premium UX indicator */}
      <div style={{
        position: 'absolute', bottom: '16px', left: 0, right: 0,
        display: 'flex', justifyContent: 'center', gap: '8px',
        opacity: 1,
        transition: 'opacity 0.3s ease',
        zIndex: 10
      }}>
        {promoImages.map((_, i) => (
          <div key={i} style={{
            width: i === index ? '20px' : '8px',
            height: '8px', borderRadius: '4px',
            backgroundColor: i === index ? '#ff4757' : 'rgba(255,255,255,0.6)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            transition: 'all 0.3s'
          }} />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const MegaDealsSection: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const promoRef = useRef<HTMLDivElement>(null);
  const [activeCat, setActiveCat] = useState(0);
  const { h, m, s } = useCountdown(5);
  const pad = (n: number) => String(n).padStart(2, '0');

  const filtered = activeCat === 0
    ? dealProducts
    : dealProducts.filter(p => p.category === dealCategories[activeCat].label);

  const scroll = (dir: 'left' | 'right') =>
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -380 : 380, behavior: 'smooth' });

  // Auto scroll mobile carousel for promo banners
  useEffect(() => {
    const el = promoRef.current;
    if (!el) return;

    let intervalId: any;
    let direction = 1; // 1 for right, -1 for left

    const autoScroll = () => {
      if (window.innerWidth > 768) return;

      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      if (maxScrollLeft <= 0) return;

      const scrollStep = window.innerWidth <= 600 ? 252 : 292;
      let nextScroll = el.scrollLeft + (direction * scrollStep);

      if (nextScroll >= maxScrollLeft - 10) {
        nextScroll = maxScrollLeft;
        direction = -1;
      } else if (nextScroll <= 10) {
        nextScroll = 0;
        direction = 1;
      }

      el.scrollTo({
        left: nextScroll,
        behavior: 'smooth'
      });
    };

    intervalId = setInterval(autoScroll, 3500);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section className="mds-root">
      <style>{`
        /* ══════════════════════════════════════════════
           MegaDealsSection — Full Responsive Styles
           ══════════════════════════════════════════════ */

        /* Root */
        .mds-root {
          padding: 28px 20px 36px;
          max-width: 1580px;
          margin: 0 auto;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          box-sizing: border-box;
        }

        /* ── Header ── */
        .mds-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 16px;
        }
        .mds-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          flex: 1;
          min-width: 0;
        }
        .mds-title {
          font-size: 20px;
          font-weight: 800;
          color: #1a1a1a;
          display: flex;
          align-items: center;
          gap: 7px;
          margin: 0;
          white-space: nowrap;
        }
        .mds-flash-icon {
          color: #ff5722;
          flex-shrink: 0;
          animation: mds-pulse 1.3s ease-in-out infinite;
        }
        @keyframes mds-pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.3); }
        }

        /* Countdown */
        .mds-countdown {
          display: flex;
          align-items: center;
          gap: 5px;
          background: linear-gradient(135deg, #1565c0 0%, #0d9488 100%);
          color: #fff;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 3px 10px rgba(21,101,192,.35);
          white-space: nowrap;
        }
        .mds-cd-label { opacity: .85; font-size: 11px; }
        .mds-cd-block {
          background: rgba(255,255,255,.25);
          border-radius: 4px;
          min-width: 28px;
          text-align: center;
          font-size: 14px;
          font-weight: 800;
          padding: 2px 4px;
          letter-spacing: 1px;
        }
        .mds-cd-sep { opacity: .65; }

        /* Header right */
        .mds-header-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .mds-view-all {
          background: transparent;
          color: #1565c0;
          border: 1.5px solid #1565c0;
          border-radius: 4px;
          padding: 7px 16px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          letter-spacing: .5px;
          transition: all .2s;
          white-space: nowrap;
        }
        .mds-view-all:hover {
          background: #1565c0;
          color: #fff;
        }
        .mds-scroll-btn {
          width: 34px; height: 34px;
          border-radius: 50%;
          border: none;
          background: #e3f2fd;
          color: #1565c0;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: background .2s, box-shadow .2s;
          flex-shrink: 0;
        }
        .mds-scroll-btn:hover { background: #bbdefb; box-shadow: 0 2px 8px rgba(21,101,192,.2); }

        /* ── Category Pills ── */
        .mds-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 18px;
          overflow-x: auto;
          padding-bottom: 4px;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .mds-tabs::-webkit-scrollbar { display: none; }
        .mds-tab {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 7px 15px;
          border-radius: 24px;
          border: 1.5px solid #e0e0e0;
          background: #fff;
          font-size: 12.5px;
          font-weight: 600;
          color: #444;
          cursor: pointer;
          white-space: nowrap;
          transition: all .2s ease;
          flex-shrink: 0;
        }
        .mds-tab:hover {
          border-color: #00a45f;
          color: #00a45f;
          background: #f0fff8;
        }
        .mds-tab.active {
          background: linear-gradient(135deg, #00a45f 0%, #1565c0 100%);
          color: #fff;
          border-color: #00a45f;
          box-shadow: 0 4px 12px rgba(0,164,95,.3);
        }

        /* ── Scroll Row ── */
        .mds-scroll-row {
          display: flex;
          gap: 14px;
          overflow-x: auto;
          scroll-behavior: smooth;
          -ms-overflow-style: none;
          scrollbar-width: none;
          padding: 6px 2px 14px;
        }
        .mds-scroll-row::-webkit-scrollbar { display: none; }

        /* ── Card ── */
        .mds-card {
          min-width: 260px;
          max-width: 260px;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
          box-shadow: 0 2px 10px rgba(0,0,0,.06);
          overflow: hidden;
          cursor: pointer;
          position: relative;
          transition: transform .28s ease, box-shadow .28s ease;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
        }
        .mds-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 14px 30px rgba(0,0,0,.12);
        }

        /* Ribbon */
        .mds-ribbon {
          position: absolute;
          top: 0; right: 0;
          background: linear-gradient(135deg, #1565c0, #0d9488);
          color: #fff;
          font-size: 10.5px;
          font-weight: 800;
          line-height: 1.2;
          text-align: center;
          padding: 5px 8px 4px;
          border-bottom-left-radius: 10px;
          z-index: 4;
        }

        /* Wishlist */
        .mds-wish-btn {
          position: absolute;
          top: 8px; left: 8px;
          background: #fff;
          border: none;
          border-radius: 50%;
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          z-index: 4;
          box-shadow: 0 2px 6px rgba(0,0,0,.12);
          transition: transform .2s;
        }
        .mds-wish-btn:hover { transform: scale(1.18); }

        /* Badge */
        .mds-badge {
          position: absolute;
          top: 42px; left: 0;
          font-size: 9.5px;
          font-weight: 800;
          letter-spacing: .5px;
          padding: 3px 10px 3px 7px;
          border-radius: 0 10px 10px 0;
          z-index: 4;
        }

        /* Image */
        .mds-img-wrap {
          width: 100%; height: 140px;
          overflow: hidden;
          background: #f8f8f8;
          display: flex; align-items: center; justify-content: center;
        }
        .mds-img {
          width: 100%; height: 100%;
          object-fit: cover;
          transition: transform .4s ease;
        }
        .mds-card:hover .mds-img { transform: scale(1.07); }

        /* Body */
        .mds-body {
          padding: 11px 11px 13px;
          display: flex;
          flex-direction: column;
          gap: 5px;
          flex: 1;
        }
        .mds-brand {
          font-size: 10.5px;
          font-weight: 700;
          color: #999;
          text-transform: uppercase;
          letter-spacing: .6px;
        }
        .mds-name {
          font-size: 13px;
          font-weight: 600;
          color: #1a1a1a;
          line-height: 1.4;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          height: 36px;
        }

        /* Rating */
        .mds-rating-row {
          display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
        }
        .mds-rating-pill {
          background: #388e3c; color: #fff;
          font-size: 10.5px; font-weight: 700;
          padding: 2px 6px; border-radius: 4px;
          display: flex; align-items: center; gap: 3px;
        }
        .mds-rating-count { font-size: 10.5px; color: #999; }
        .mds-assured {
          display: flex; align-items: center; gap: 3px;
          font-size: 10px; font-weight: 700;
          color: #1565c0;
        }

        /* Pricing */
        .mds-price-row {
          display: flex; align-items: baseline; gap: 5px; flex-wrap: wrap; margin-top: 1px;
        }
        .mds-price  { font-size: 16px; font-weight: 800; color: #1a1a1a; }
        .mds-mrp    { font-size: 12px; color: #bbb; text-decoration: line-through; }
        .mds-off    { font-size: 11.5px; font-weight: 700; color: #388e3c; }

        /* Tag pill */
        .mds-tag-pill {
          display: inline-flex; align-items: center; gap: 3px;
          background: #fff8e1; color: #f57f17;
          font-size: 10px; font-weight: 700;
          padding: 3px 8px; border-radius: 20px;
          border: 1px dashed #ffe082;
          width: fit-content;
        }

        /* ══════════════════════════════════════════════
           button-48  —  same clip-path swipe animation
           as ProductCard.tsx — Blue+Green theme
           ══════════════════════════════════════════════ */
        .mds-btn48 {
          appearance: none;
          background: linear-gradient(135deg, #00a45f 0%, #1565c0 100%);
          border-width: 0;
          box-sizing: border-box;
          color: #fff;
          cursor: pointer;
          display: block;
          width: 100%;
          font-family: Clarkson, Helvetica, sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: .3px;
          line-height: 1em;
          margin: 0;
          margin-top: 10px;
          opacity: 1;
          outline: 0;
          padding: 1.1em 1em;
          position: relative;
          text-align: center;
          text-rendering: geometricprecision;
          text-transform: uppercase;
          transition:
            opacity 300ms cubic-bezier(.694, 0, .335, 1),
            background-color 100ms cubic-bezier(.694, 0, .335, 1),
            color 100ms cubic-bezier(.694, 0, .335, 1);
          user-select: none;
          -webkit-user-select: none;
          touch-action: manipulation;
          border-radius: 4px;
          overflow: hidden;
        }

        .mds-btn48::before {
          animation: mds-opacityOut .5s step-end forwards;
          backface-visibility: hidden;
          background-color: #00c96a;
          clip-path: polygon(-1% 0, 0 0, -25% 100%, -1% 100%);
          content: '';
          height: 100%;
          left: 0;
          position: absolute;
          top: 0;
          transform: translateZ(0);
          transition:
            clip-path .5s cubic-bezier(.165, .84, .44, 1),
            -webkit-clip-path .5s cubic-bezier(.165, .84, .44, 1);
          width: 100%;
        }
        .mds-btn48:hover::before {
          animation: mds-opacityIn 0s step-start forwards;
          clip-path: polygon(0 0, 101% 0, 101% 101%, 0 101%);
        }
        .mds-btn48::after { background-color: #fff; }
        .mds-btn48__text {
          position: relative;
          z-index: 1;
          pointer-events: none;
        }

        /* Added state — blue */
        .mds-btn48--added {
          background-color: #1976d2 !important;
        }
        .mds-btn48--added::before {
          background-color: #42a5f5;
        }

        @keyframes mds-opacityIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes mds-opacityOut {
          0%   { opacity: 1; }
          100% { opacity: 0; }
        }

        /* ── Promo Strip ── */
        .mds-promo-strip {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-top: 22px;
        }
        .mds-promo-card {
          border-radius: 10px;
          overflow: hidden;
          position: relative;
          cursor: pointer;
          box-shadow: 0 3px 10px rgba(0,0,0,.1);
          transition: transform .25s, box-shadow .25s;
          height: 350px;
        }
        .mds-promo-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(0,0,0,.15); }
        .mds-promo-card img {
          width: 100%; height: 100%;
          object-fit: cover; display: block;
          transition: transform .4s;
        }
        .mds-promo-card:hover img { transform: scale(1.06); }
        .mds-promo-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,.65) 0%, transparent 60%);
          display: flex; flex-direction: column;
          justify-content: flex-end;
          padding: 11px 13px;
        }
        .mds-promo-title { color: #fff; font-size: 20.5px; font-weight: 800; text-shadow: 0 1px 4px rgba(0,0,0,.4); }
        .mds-promo-sub   { color: rgba(255,255,255,.82); font-size: 15px; margin-top: 2px; }

        /* ══════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
           ══════════════════════════════════════════════ */

        /* Tablet – 1024px */
        @media (max-width: 1024px) {
          .mds-card { min-width: 240px; max-width: 240px; }
          .mds-img-wrap { height: 130px; }
        }

        /* Tablet portrait – 768px */
        @media (max-width: 768px) {
          .mds-root { padding: 20px 14px 28px; }
          .mds-title { font-size: 17px; }
          .mds-countdown { font-size: 11px; padding: 5px 10px; }
          .mds-cd-block { font-size: 13px; min-width: 25px; }
          .mds-card { min-width: 210px; max-width: 210px; }
          .mds-img-wrap { height: 120px; }
          .mds-price { font-size: 14px; }
          .mds-promo-strip {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            scrollbar-width: none !important;
            gap: 12px !important;
            padding: 4px 2px 14px !important;
            margin-top: 16px !important;
          }
          .mds-promo-strip::-webkit-scrollbar {
            display: none !important;
          }
          .mds-promo-card {
            width: 280px !important;
            min-width: 280px !important;
            flex-shrink: 0 !important;
            height: 180px !important;
            box-sizing: border-box !important;
          }
          .mds-promo-last {
            display: block !important;
          }
          .mds-btn48 { font-size: 11px; padding: .95em .8em; }
        }

        /* Mobile – 600px */
        @media (max-width: 600px) {
          .mds-root { padding: 16px 10px 24px; }
          .mds-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
            margin-bottom: 12px !important;
          }
          .mds-header-left {
            width: 100% !important;
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
          }
          .mds-title { font-size: 15px; }
          .mds-countdown {
            display: flex !important;
            font-size: 11px !important;
            padding: 4px 8px !important;
            box-shadow: 0 2px 6px rgba(21,101,192,.2) !important;
          }
          .mds-cd-block {
            font-size: 12px !important;
            min-width: 22px !important;
            padding: 1px 3px !important;
          }
          .mds-view-all { padding: 6px 12px; font-size: 11px; }
          .mds-card { min-width: 180px; max-width: 180px; }
          .mds-img-wrap { height: 108px; }
          .mds-body { padding: 9px 9px 11px; gap: 4px; }
          .mds-name { font-size: 12px; height: 32px; }
          .mds-price { font-size: 13px; }
          .mds-mrp, .mds-off { font-size: 10.5px; }
          .mds-btn48 { font-size: 10.5px; padding: .9em .7em; margin-top: 8px; }
          .mds-promo-card {
            width: 240px !important;
            min-width: 240px !important;
            flex-shrink: 0 !important;
            height: 150px !important;
            box-sizing: border-box !important;
          }
          .mds-scroll-btn { display: none; }
        }

        /* Mobile XS – 360px */
        @media (max-width: 360px) {
          .mds-card { min-width: 165px; max-width: 165px; }
          .mds-img-wrap { height: 100px; }
          .mds-title { font-size: 14px; }
        }
      `}</style>

      {/* ── Header ── */}
      <div className="mds-header">
        <div className="mds-header-left">
          <h2 className="mds-title">
            <MdFlashOn className="mds-flash-icon" size={24} />
            Flash Sale Deals
          </h2>
          <div className="mds-countdown">
            <span className="mds-cd-label">Ends in</span>
            <span className="mds-cd-block">{pad(h)}</span>
            <span className="mds-cd-sep">:</span>
            <span className="mds-cd-block">{pad(m)}</span>
            <span className="mds-cd-sep">:</span>
            <span className="mds-cd-block">{pad(s)}</span>
          </div>
        </div>
        <div className="mds-header-right">
          {/* <button className="mds-view-all">VIEW ALL</button> */}
          <button className="mds-scroll-btn" onClick={() => scroll('left')} aria-label="Scroll Left">
            <BiChevronLeft size={21} />
          </button>
          <button className="mds-scroll-btn" onClick={() => scroll('right')} aria-label="Scroll Right">
            <BiChevronRight size={21} />
          </button>
        </div>
      </div>

      {/* ── Category Tabs ── */}
      {/* <div className="mds-tabs" role="tablist">
        {dealCategories.map(cat => (
          <button
            key={cat.id}
            id={`mds-tab-${cat.id}`}
            role="tab"
            aria-selected={activeCat === cat.id}
            className={`mds-tab${activeCat === cat.id ? ' active' : ''}`}
            onClick={() => setActiveCat(cat.id)}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </button>
        ))}
      </div> */}

      {/* ── Product Cards Row ── */}
      {/* <div ref={scrollRef} className="mds-scroll-row" id="mds-products-row">
        {filtered.map(p => <DealCard key={p.id} product={p} />)}
        {filtered.length === 0 && (
          <div style={{ padding: '36px 0', color: '#aaa', fontSize: '14px', width: '100%', textAlign: 'center' }}>
            No deals in this category right now.
          </div>
        )}
      </div> */}

      {/* ── Promo Banner Strip ── */}
      <div ref={promoRef} className="mds-promo-strip">
        <div className="mds-promo-card">
          <img src="https://images.unsplash.com/photo-1445205170230-053b83016050?w=600&q=80" alt="Fashion Sale" />
          <div className="mds-promo-overlay">
            <div className="mds-promo-title">Fashion Bonanza</div>
            <div className="mds-promo-sub">Min. 70% Off</div>
          </div>
        </div>
        <HoverCarouselCard />
        <div className="mds-promo-card mds-promo-last">
          <img src="https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=600&q=80" alt="Beauty" />
          <div className="mds-promo-overlay">
            <div className="mds-promo-title">Beauty Picks</div>
            <div className="mds-promo-sub">Starting ₹199</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MegaDealsSection;
